import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { loadFonts } from './utils/fontLoader';
import { deferScript } from './utils/performanceUtils';

// Load fonts asynchronously to prevent render blocking
loadFonts();

// Defer loading of third-party scripts
const loadThirdPartyScripts = async () => {
  // Defer Stripe loading until needed (checkout pages)
  if (window.location.pathname.includes('checkout') ||
      window.location.pathname.includes('subscription') ||
      window.location.pathname.includes('pricing')) {
    await deferScript('https://js.stripe.com/v3/');
  }

  // Defer Google Analytics to prevent render blocking
  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-XHSHYPK0K6';

  gtagScript.onload = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'G-XHSHYPK0K6', {
      send_page_view: true,
    });
  };

  document.head.appendChild(gtagScript);
};

// Start loading third-party scripts after initial render
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    loadThirdPartyScripts();
  });
} else {
  setTimeout(loadThirdPartyScripts, 1);
}

// Register service worker with better timing
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    navigator.serviceWorker.register('/service-worker.js', {
      updateViaCache: 'none'
    }).then(registration => {
      console.log('Service Worker registered:', registration);
    }).catch(error => {
      console.error('Service Worker registration failed:', error);
    });
  }
};

// Defer service worker registration
if ('requestIdleCallback' in window) {
  requestIdleCallback(registerServiceWorker, { timeout: 2000 });
} else {
  (window as Window).addEventListener('load', () => {
    setTimeout(registerServiceWorker, 1000);
  });
}

// Render app
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Hide initial loading spinner if present (moved from inline script to satisfy CSP)
(() => {
  const hideLoader = () => {
    const loader = document.querySelector('.loading-container') as HTMLElement | null;
    if (loader) loader.style.display = 'none';
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(hideLoader, 100), { once: true });
  } else {
    setTimeout(hideLoader, 100);
  }
})();

// Report web vitals for monitoring (lazy load)
if (import.meta.env.MODE === 'production') {
  import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
    const sendToAnalytics = (metric: any) => {
      if (window.gtag) {
        window.gtag('event', metric.name, {
          value: Math.round(metric.value),
          metric_id: metric.id,
          metric_delta: metric.delta,
        });
      }
    };

    onCLS(sendToAnalytics);
    onINP(sendToAnalytics);
    onFCP(sendToAnalytics);
    onLCP(sendToAnalytics);
    onTTFB(sendToAnalytics);
  });
}
